import { useAuth0 } from "@auth0/auth0-react";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Divider, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { ToggleButton } from "@/Components/v2/atoms/Button";
import { Dialog } from "@/Components/v2/atoms/Dialog";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { ABBREVIATED_OPTION } from "@/hooks/useConfig";
import { ALL_GEOGRAPHIES_LABEL } from "@/pages/scenario-builder/constants";
import { CreateScenarioResponse } from "@/pages/scenario-builder/v3/api/createScenario";
import { useCreateScenario } from "@/pages/scenario-builder/v3/api/createScenario";
import { useModelMetadata } from "@/pages/scenario-builder/v3/api/getCustomerModelMetadata";
import { useDateRanges } from "@/pages/scenario-builder/v3/api/getDateRanges";
import { useHistoricalSpends } from "@/pages/scenario-builder/v3/api/getHistoricalSpend";
import { useRunScenario } from "@/pages/scenario-builder/v3/api/runScenario";
import { useUpdateScenarioV3 } from "@/pages/scenario-builder/v3/api/updateScenario";
import {
  ScenarioType,
  ScenarioTypeEnum,
} from "@/pages/scenario-builder/v3/pages/scenario-form/types";
import { allocateForecastBudget } from "@/pages/scenario-builder/v3/pages/scenario-form/utils/logic";
import { isDefined } from "@/Utils/array-utils";

import { ScenarioSummary } from "./ScenarioSummary";
import { StyledSelect, StyledSlider } from "./StyledQuickSimulateFormComponents";
import { useBudgetCalculation } from "./utils/useBudgetCalculation";

type GeographyOption = {
  isAllGeos: boolean;
  value: string;
};

export interface QuickSimulateGeoFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onSimulationComplete?: () => void;
  defaultBusinessUnit?: string;
  defaultGeography?: string;
}

export const QuickSimulateGeoForm: React.FC<QuickSimulateGeoFormProps> = ({
  open,
  onClose,
  onSubmit,
  onSimulationComplete,
  defaultBusinessUnit,
  defaultGeography,
}) => {
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);
  const [months, setMonths] = useState(6); // Default 6 months
  const [budgetPercentage, setBudgetPercentage] = useState(0); // Default 0% (current budget)
  const [businessUnit, setBusinessUnit] = useState("");
  const [geography, setGeography] = useState<string | null>(null);
  const [scenarioType, setScenarioType] = useState<"budget" | "optimizer">("budget");
  const [budgetMetricType, setBudgetMetricType] = useState<"percentage" | "amount">("percentage");

  const { getBCPHierarchyLabel } = useDefaultNodeNameInfo();
  const { scenarioCreated, scenarioSubmitted } = useAnalytics();
  const { user } = useAuth0();
  const { config } = useConfig();
  const formatCurrency = config.currency.formatCurrency;
  const theme = useTheme();

  // Quick Scenario business logic hooks
  const { data: metaData, isLoading: isLoadingMetadata } = useModelMetadata({
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  const brandGeographies = metaData?.brand_geographies;

  const { data: dateRanges } = useDateRanges({
    geography: geography || undefined,
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  // State for Quick Scenario parameters to track changes
  // Note: This state mirrors the main form state for parameter change callbacks
  const [quickSimulateParams, setQuickSimulateParams] = useState<{
    businessUnit: string | null;
    geography: string | null;
    months: number;
    budgetPercentage: number;
  }>({
    businessUnit: null,
    geography: null,
    months: 6,
    budgetPercentage: 0,
  });

  // Calculate dynamic date range based on selected months
  const quickSimulateStartDate = useMemo(() => {
    if (!dateRanges) return null;
    return dayjs.utc(dateRanges.startDate).add(1, "month").startOf("month");
  }, [dateRanges]);

  const quickSimulateEndDate = useMemo(() => {
    if (!quickSimulateStartDate) return null;
    return quickSimulateStartDate
      .add(quickSimulateParams.months, "month")
      .subtract(1, "day")
      .endOf("month");
  }, [quickSimulateStartDate, quickSimulateParams.months]);

  // Calculate scope geographies for historical spend fetching
  // This logic matches the pattern used in ScenarioFormProvider to ensure consistent budget calculations
  const scopeGeographies = useMemo(() => {
    if (!businessUnit) return undefined;

    // If no geography is selected (All Geographies), return undefined to omit the geographies parameter
    // This allows the backend to return data for all geographies available for the selected brand
    // This matches the ScenarioForm behavior: no geographies parameter = all geographies for the brand
    if (!geography) {
      return undefined;
    }

    const brandGeographyData = brandGeographies?.find((bg) => bg.brand === businessUnit);

    // If the selected geography is not available for this business unit, return empty array
    if (!brandGeographyData?.geographies.includes(geography)) {
      return [];
    }

    return [geography];
  }, [geography, businessUnit, brandGeographies]);

  // Historical spends for budget calculation - refetch when business unit, geography, or dates change
  const { data: historicalSpendsDTO, refetch: refetchHistoricalSpends } = useHistoricalSpends({
    config: {
      refetchOnWindowFocus: false,
      enabled:
        !!businessUnit && !!quickSimulateStartDate && !!quickSimulateEndDate && !!brandGeographies,
    },
    startDate: quickSimulateStartDate?.format("YYYY-MM-DD"),
    endDate: quickSimulateEndDate?.format("YYYY-MM-DD"),
    brand: businessUnit || undefined,
    geographies: scopeGeographies,
    isScenariosApiEnabled,
  });

  // Force refetch when geography changes to ensure fresh data
  const previousGeography = useRef<string | null>(null);
  useEffect(() => {
    if (
      previousGeography.current !== geography &&
      businessUnit &&
      quickSimulateStartDate &&
      quickSimulateEndDate
    ) {
      refetchHistoricalSpends();
    }
    previousGeography.current = geography;
  }, [
    geography,
    refetchHistoricalSpends,
    businessUnit,
    quickSimulateStartDate,
    quickSimulateEndDate,
    scopeGeographies,
  ]);

  // Calculate historical spend base for budget calculation (used for percentage calculations)
  const historicalSpendBase = useMemo(() => {
    if (!historicalSpendsDTO || historicalSpendsDTO.length === 0) {
      return 0;
    }

    return historicalSpendsDTO.reduce((acc, spend) => acc + spend.marketing_spend.spend, 0);
  }, [historicalSpendsDTO]);

  // Calculate budget amounts using custom hook
  const { budgetAmount, maxAllowedBudget } = useBudgetCalculation({
    historicalSpendBase,
    budgetPercentage,
    historicalSpendsDTO,
    brand: businessUnit || undefined,
    geography: geography || undefined, // Use actual geography selection (null = all geographies)
  });

  // Quick Simulate mutations
  const quickSimulateCreateScenario = useCreateScenario({
    config: {
      onSuccess: (result: CreateScenarioResponse) => {
        if (result.uuid) {
          scenarioCreated({
            scenarioType: ScenarioType.toString(result.scenarioType as ScenarioTypeEnum),
            scenarioId: result.uuid,
            scenarioName: result.name || "Untitled Scenario",
            isCloned: false,
            isQuickScenario: true,
          });
        }
        // No navigation for Quick Simulate
      },
    },
    isScenariosApiEnabled,
  });

  const runScenario = useRunScenario({ isScenariosApiEnabled });
  const updateScenario = useUpdateScenarioV3({ isScenariosApiEnabled });

  // Set default business unit and geography when brandGeographies becomes available
  useEffect(() => {
    if (!brandGeographies?.length || businessUnit) {
      return;
    }

    let selectedBusinessUnit: string | null = null;
    let selectedGeography: string | null = null;

    // If defaultBusinessUnit exists and is in the list, use it
    if (defaultBusinessUnit) {
      const defaultBrandGeo = brandGeographies.find((bg) => bg.brand === defaultBusinessUnit);
      if (defaultBrandGeo) {
        selectedBusinessUnit = defaultBusinessUnit;

        // If defaultGeography exists and is valid for this business unit, use it
        if (defaultGeography && defaultBrandGeo.geographies.includes(defaultGeography)) {
          selectedGeography = defaultGeography;
        } else {
          // Default to "All Geographies" if no specific geography is provided
          selectedGeography = null;
        }
      }
    }

    // Otherwise, use the first business unit and default to "All Geographies"
    if (!selectedBusinessUnit && brandGeographies[0]) {
      selectedBusinessUnit = brandGeographies[0].brand;
      selectedGeography = null; // Default to "All Geographies"
    }

    if (selectedBusinessUnit) {
      setBusinessUnit(selectedBusinessUnit);
      setGeography(selectedGeography);
      setQuickSimulateParams({
        businessUnit: selectedBusinessUnit,
        geography: selectedGeography,
        months,
        budgetPercentage,
      });
    }
  }, [
    brandGeographies,
    businessUnit,
    defaultBusinessUnit,
    defaultGeography,
    months,
    budgetPercentage,
  ]);

  const handleMonthsChange = (_: Event, newValue: number | number[]) => {
    const newMonths = newValue as number;
    setMonths(newMonths);
    setQuickSimulateParams((prev) => ({
      ...prev,
      months: newMonths,
    }));
  };

  const handleBudgetChange = (_: Event, newValue: number | number[]) => {
    const newBudgetPercentage = Math.max(newValue as number, -95);
    setBudgetPercentage(newBudgetPercentage);
    setQuickSimulateParams((prev) => ({
      ...prev,
      budgetPercentage: newBudgetPercentage,
    }));
  };

  const handleBusinessUnitChange = (value: string) => {
    setBusinessUnit(value);

    // Reset geography to "All Geographies" when business unit changes
    const newGeography = null; // Default to "All Geographies"
    setGeography(newGeography);

    setQuickSimulateParams((prev) => ({
      ...prev,
      businessUnit: value,
      geography: newGeography,
    }));
  };

  const handleGeographyChange = (value: GeographyOption | null) => {
    const newGeography = value?.isAllGeos ? null : value?.value || null;
    setGeography(newGeography);
    setQuickSimulateParams((prev) => ({
      ...prev,
      geography: newGeography,
    }));
  };

  // Handle Quick Simulate - moved from ScenariosPage
  const handleQuickSimulate = async () => {
    try {
      // Handle modal close, navigation
      onSubmit();

      // Use the pre-calculated start and end dates
      const startDate = quickSimulateStartDate!;
      const endDate = quickSimulateEndDate!;

      // Use calculated budget amount from hook
      const budgetAmountForScenario = budgetAmount;

      // Generate scenario name with new format: "Business Unit, Geography, Budget, Date Range"
      const scenarioTypeName = scenarioType === "budget" ? "Forecast" : "Optimizer";

      const startDateFormatted = startDate.format("Do MMM YYYY");
      const endDateFormatted = endDate.format("Do MMM YYYY");
      const budgetFormatted = formatCurrency(budgetAmount, ABBREVIATED_OPTION);

      // Get formatted business unit label
      const businessUnitLabel = getBCPHierarchyLabel({
        brand: businessUnit,
        category: "default",
        product: "default",
      });

      const geographyLabel = geography || "National";
      const scenarioName = `${businessUnitLabel} - ${geographyLabel}, ${budgetFormatted}, ${startDateFormatted} - ${endDateFormatted}`;

      // Show toast notification
      toast.info("Quick scenario simulating");

      // Create new scenario with basic info
      const newScenario = await quickSimulateCreateScenario.mutateAsync({
        scenarioType: scenarioType,
        createdBy: user!.email!,
        name: scenarioName,
        scenarioCount: scenarioType === "optimizer" ? 1000 : null,
      });

      if (!newScenario.uuid) {
        throw new Error("Failed to create scenario - no UUID returned");
      }

      // Calculate allocated spends for budget allocation (needed for forecast run request)
      // historicalSpendsDTO is already filtered by the selected business unit and geography
      const allocatedSpends = allocateForecastBudget(
        historicalSpendsDTO || [],
        budgetAmountForScenario,
        [] // No constraints
      );

      // Build scenario constraints - empty for Quick Simulate scenarios (no spend limits)
      const scenarioConstraints: any[] = [];

      // Update the scenario with all required fields
      const updateScenarioRequest = {
        name: scenarioName,
        description: `Quick ${scenarioTypeName}: ${businessUnitLabel}, ${geographyLabel}, ${budgetFormatted} Budget, ${startDateFormatted} - ${endDateFormatted}`,
        brand: businessUnit,
        geography: geography,
        spendGeographicGranularity: geography
          ? ("single_geography" as const)
          : ("all_geographies" as const),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        value: budgetAmountForScenario,
        scenarioConstraints,
      };

      await updateScenario.mutateAsync({
        scenarioUUID: newScenario.uuid,
        scenario: updateScenarioRequest,
        scenarioType,
      });

      // Run the scenario with proper request structure
      const runRequest =
        scenarioType === "optimizer"
          ? {
              type: "optimizer" as const,
              optimizerMediaConstraints: [], // No constraints for optimizer
            }
          : {
              type: "budget" as const,
              allocatedSpends:
                allocatedSpends?.map((spend) => ({
                  geography: spend.geography,
                  spend: spend.spend,
                  channel: spend.channel,
                  product: spend.product,
                  category: spend.category,
                  brand: spend.brand,
                })) || [],
            };

      await runScenario.mutateAsync({
        scenarioId: newScenario.uuid,
        runScenarioRequest: runRequest,
      });

      // Track scenario submission with quick scenario parameters
      scenarioSubmitted({
        scenarioName: scenarioName,
        scenarioId: newScenario.uuid,
        scenarioType: scenarioType,
        budgetRatio: budgetPercentage,
        scenarioLengthMonths: months,
        mediaSpendInputs: scenarioType === "budget" ? 0 : undefined,
        mediaSpendConstraints: scenarioType === "optimizer" ? 0 : undefined,
        isQuickScenario: true,
        version: 3,
      });

      // Success feedback
      toast.success(
        `Quick ${scenarioTypeName} simulation started! Check the scenarios list for results.`
      );

      // Notify parent component to refresh scenarios
      if (onSimulationComplete) {
        onSimulationComplete();
      }
    } catch (error) {
      console.error("Quick build error:", error);
      toast.error("Failed to start quick build");
    }
  };

  const businessUnitOptions = useMemo(() => {
    return brandGeographies?.map((brandGeography) => ({
      value: brandGeography.brand,
      label: getBCPHierarchyLabel({
        brand: brandGeography.brand,
        category: "default",
        product: "default",
      }),
    }));
  }, [brandGeographies, getBCPHierarchyLabel]);

  const geographyOptions = useMemo(() => {
    if (!businessUnit) return [];

    const selectedBrandGeo = brandGeographies?.find((bg) => bg.brand === businessUnit);
    const specificGeographies =
      selectedBrandGeo?.geographies.map((geo) => ({
        isAllGeos: false,
        value: geo,
      })) || [];

    // Add "All Geographies" option at the beginning
    return [
      {
        isAllGeos: true,
        value: ALL_GEOGRAPHIES_LABEL,
      },
      ...specificGeographies,
    ];
  }, [businessUnit, brandGeographies]);

  // Convert internal geography state to GeographyOption for Autocomplete
  const currentGeographyOption = useMemo(() => {
    return geography
      ? { isAllGeos: false, value: geography }
      : { isAllGeos: true, value: ALL_GEOGRAPHIES_LABEL };
  }, [geography]);

  const monthsMarks = [
    { value: 1, label: "1m" },
    { value: 3, label: "3m" },
    { value: 6, label: "6m" },
    { value: 9, label: "9m" },
    { value: 12, label: "12m" },
  ];

  const calculateDifferenceFromHistoricalSpend = useCallback(
    (percentage: number) => {
      if (percentage === 0) return 0;
      return historicalSpendBase * (1 + percentage / 100) - historicalSpendBase;
    },
    [historicalSpendBase]
  );

  const getBudgetMarkLabel = (percentage: number) => {
    const labelPercentage = percentage === -95 ? -100 : percentage;
    if (budgetMetricType === "percentage") {
      if (percentage > 0) return `+${percentage}%`;
      return `${labelPercentage}%`;
    }
    const budgetChange = calculateDifferenceFromHistoricalSpend(labelPercentage);
    const formattedCurrency = formatCurrency(budgetChange, ABBREVIATED_OPTION);

    if (percentage > 0) return `+${formattedCurrency}`;
    return formattedCurrency;
  };

  const budgetMarks = [
    { value: -95, label: getBudgetMarkLabel(-95) },
    { value: -50, label: getBudgetMarkLabel(-50) },
    { value: 0, label: getBudgetMarkLabel(0) },
    { value: 50, label: getBudgetMarkLabel(50) },
    { value: 100, label: getBudgetMarkLabel(100) },
  ];

  const scenarioTypeOptions = [
    { label: "Forecast", value: "budget" as const },
    { label: "Optimizer", value: "optimizer" as const },
  ];

  const budgetSliderMetricOptions = [
    { label: "%", value: "percentage" as const },
    { label: config.currency.getSymbol(), value: "amount" as const },
  ];

  const isSimulating =
    quickSimulateCreateScenario.isPending || runScenario.isPending || updateScenario.isPending;

  // Form controls should only be disabled when basic metadata is unavailable
  const isFormDisabled = isLoadingMetadata || !brandGeographies;

  // Simulate button should be disabled when any required data for simulation is missing
  const isSimulateDisabled =
    isFormDisabled ||
    !user?.email ||
    !dateRanges ||
    !quickSimulateStartDate ||
    !quickSimulateEndDate ||
    !businessUnit ||
    !isDefined(maxAllowedBudget);

  const getBusinessUnitLabel = (businessUnit: string) => {
    const option = businessUnitOptions?.find((opt) => opt.value === businessUnit);
    return option?.label || businessUnit;
  };

  const budgetChange = useMemo(() => {
    if (!historicalSpendBase) return "";
    const sign = budgetPercentage > 0 ? "+" : "";
    if (budgetMetricType === "percentage") {
      return `${sign}${budgetPercentage}% from historical budget`;
    }
    return `${sign}${formatCurrency(calculateDifferenceFromHistoricalSpend(budgetPercentage))} from historical budget`;
  }, [
    historicalSpendBase,
    budgetPercentage,
    budgetMetricType,
    calculateDifferenceFromHistoricalSpend,
    formatCurrency,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <Stack
        direction="column"
        sx={{ backgroundColor: theme.palette.quickSimulate.backgroundColor }}
      >
        <Stack direction="row" spacing={3}>
          <Stack spacing={4} sx={{ px: 6, py: 3 }} width={400}>
            <Typography variant="h6" fontWeight={600} fontSize={24} textAlign="center">
              Quick Scenario
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={scenarioType}
              fullWidth
              sx={{ display: "flex", justifyContent: "center" }}
            >
              {scenarioTypeOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  onClick={() => setScenarioType(option.value)}
                  disabled={isFormDisabled || isSimulating}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl fullWidth>
              <Typography variant="body2" fontWeight={600} mb={1} fontSize={14}>
                Business Unit
              </Typography>
              <StyledSelect
                value={businessUnit}
                onChange={(event) => handleBusinessUnitChange(event.target.value)}
                size="small"
                displayEmpty
                disabled={isFormDisabled || isSimulating || !brandGeographies?.length}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: "text.primary", fontSize: "14px" }}>
                        Select an option
                      </Typography>
                    );
                  }
                  return getBusinessUnitLabel(selected);
                }}
              >
                {businessUnitOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>

            <FormControl fullWidth>
              <Typography
                variant="body2"
                fontWeight={600}
                mb={1}
                fontSize={14}
                sx={{ color: "text.primary" }}
              >
                Geography
              </Typography>
              <Autocomplete<GeographyOption | null, false, true>
                value={currentGeographyOption}
                getOptionLabel={(option) => option?.value ?? ""}
                options={geographyOptions}
                onChange={(_event, value) => {
                  handleGeographyChange(value);
                }}
                disableClearable
                disabled={isFormDisabled || isSimulating || !businessUnit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select geography"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "14px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (!option) {
                    return null;
                  }

                  if (option.isAllGeos) {
                    return (
                      <>
                        <Typography {...props} sx={{ color: "text.primary" }}>
                          {option.value}
                        </Typography>
                        <Divider sx={{ marginY: "0.5rem" }} />
                      </>
                    );
                  }
                  return (
                    <Typography {...props} sx={{ color: "text.primary" }}>
                      {option.value}
                    </Typography>
                  );
                }}
              />
            </FormControl>

            <Box>
              <Typography fontWeight={600} variant="body2" mb={2} fontSize={14}>
                Duration:
              </Typography>
              <Box px={2}>
                <StyledSlider
                  value={months}
                  onChange={handleMonthsChange}
                  min={1}
                  max={12}
                  step={1}
                  marks={monthsMarks}
                  track={false}
                  disabled={isFormDisabled || isSimulating}
                />
              </Box>
            </Box>
            <Box height={110}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={600} variant="body2" fontSize={14}>
                  Budget Change:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={budgetMetricType}
                  size="small"
                  sx={{ width: 70 }}
                >
                  {budgetSliderMetricOptions.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      onClick={() => setBudgetMetricType(option.value)}
                      sx={{ height: 35 }}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Box px={2}>
                <StyledSlider
                  value={budgetPercentage}
                  onChange={handleBudgetChange}
                  min={-100}
                  max={100}
                  step={10}
                  marks={budgetMarks}
                  track={false}
                  disabled={isFormDisabled || isSimulating}
                />
              </Box>
              {budgetPercentage === -95 && (
                <Typography
                  variant="body2"
                  fontSize={12}
                  fontStyle="italic"
                  sx={{ color: "#FC9429" }}
                >
                  Budget set to minimum value of -95%
                </Typography>
              )}
            </Box>
          </Stack>
          <ScenarioSummary
            scenarioType={scenarioType}
            businessUnit={getBusinessUnitLabel(businessUnit)}
            months={months}
            startDate={quickSimulateStartDate}
            endDate={quickSimulateEndDate}
            budgetAmount={budgetAmount}
            formatCurrency={formatCurrency}
            budgetChange={budgetChange}
            geography={currentGeographyOption?.value}
          />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={3} p={4}>
          <OutlinedButton
            onClick={onClose}
            fullWidth
            disabled={isSimulating}
            sx={{
              borderRadius: 4,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 16,
              color: "text.secondary",
              borderColor: "grey.300",
            }}
          >
            Cancel
          </OutlinedButton>
          <PrimaryButton
            onClick={handleQuickSimulate}
            fullWidth
            disabled={isSimulateDisabled || isSimulating}
            sx={{
              borderRadius: 4,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {isSimulating ? "Submitting" : "Submit Scenario"}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Dialog>
  );
};
